import { Fragment, useEffect, useMemo, useState } from 'react';
import { RotateCcw, Save, ShieldCheck } from 'lucide-react';
import type { AdminRole, RbacPermission } from '@digitaltattoo/types';
import { useRbac } from '../contexts/RbacContext';
import {
  getRbacConfiguration,
  resetRolePermissions,
  updateRolePermissions,
  type RbacConfiguration,
} from '../services/rbacApi';

const ROLE_LABELS: Record<AdminRole, string> = {
  super_admin: 'Super administrateur',
  company_admin: 'Administrateur entreprise',
  shop_admin: 'Administrateur boutique',
  manager: 'Manager',
};

export function RbacSettingsPage() {
  const { refresh: refreshMyPermissions } = useRbac();
  const [config, setConfig] = useState<RbacConfiguration | null>(null);
  const [matrix, setMatrix] = useState<Record<AdminRole, RbacPermission[]> | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingRole, setSavingRole] = useState<AdminRole | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getRbacConfiguration();
      setConfig(data);
      setMatrix(data.matrix);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger la configuration RBAC');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const categories = useMemo(
    () => [...new Set(config?.permissions.map((permission) => permission.category) ?? [])],
    [config],
  );

  const toggle = (role: AdminRole, permission: RbacPermission) => {
    if (!matrix || role === 'super_admin') return;
    setMatrix({
      ...matrix,
      [role]: matrix[role].includes(permission)
        ? matrix[role].filter((key) => key !== permission)
        : [...matrix[role], permission],
    });
    setMessage('');
  };

  const save = async (role: AdminRole) => {
    if (!matrix || role === 'super_admin') return;
    setSavingRole(role);
    setError('');
    setMessage('');
    try {
      await updateRolePermissions(role, matrix[role]);
      await refreshMyPermissions();
      setMessage(`Permissions de « ${ROLE_LABELS[role]} » enregistrées.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible d’enregistrer les permissions');
    } finally {
      setSavingRole(null);
    }
  };

  const reset = async (role: AdminRole) => {
    if (role === 'super_admin') return;
    setSavingRole(role);
    setError('');
    setMessage('');
    try {
      await resetRolePermissions(role);
      await Promise.all([load(), refreshMyPermissions()]);
      setMessage(`Permissions de « ${ROLE_LABELS[role]} » réinitialisées.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de réinitialiser les permissions');
    } finally {
      setSavingRole(null);
    }
  };

  if (loading) {
    return <div className="p-6 text-gray-500">Chargement des rôles et permissions…</div>;
  }

  if (!config || !matrix) {
    return <div className="p-6 text-red-700">{error || 'Configuration RBAC indisponible'}</div>;
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-7 w-7 text-indigo-600" />
          <h1 className="text-2xl font-semibold text-gray-900">Rôles et permissions</h1>
        </div>
        <p className="mt-2 max-w-3xl text-sm text-gray-600">
          Configurez les capacités de chaque rôle. Le super administrateur conserve toujours
          toutes les permissions afin d’éviter le verrouillage de la plateforme. Les cases
          grisées correspondent à des opérations globales qui ne peuvent pas être déléguées
          tant qu’elles ne disposent pas d’un périmètre entreprise/boutique sûr.
        </p>
      </div>

      {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {message && <div className="rounded-md bg-green-50 p-3 text-sm text-green-700">{message}</div>}

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="min-w-80 px-4 py-3 text-left text-xs font-semibold uppercase text-gray-600">
                Permission
              </th>
              {config.roles.map((role) => (
                <th key={role} className="min-w-44 px-4 py-3 text-center text-xs font-semibold uppercase text-gray-600">
                  {ROLE_LABELS[role]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {categories.map((category) => (
              <Fragment key={category}>
                <tr className="bg-gray-50">
                  <td colSpan={config.roles.length + 1} className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {category}
                  </td>
                </tr>
                {config.permissions
                  .filter((permission) => permission.category === category)
                  .map((permission) => (
                    <tr key={permission.key}>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{permission.label}</div>
                        <div className="mt-0.5 text-xs text-gray-500">{permission.description}</div>
                        <code className="mt-1 block text-xs text-indigo-700">{permission.key}</code>
                      </td>
                      {config.roles.map((role) => (
                        <td key={role} className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={matrix[role].includes(permission.key)}
                            disabled={
                              !config.editablePermissions[role].includes(permission.key) ||
                              savingRole !== null
                            }
                            onChange={() => toggle(role, permission.key)}
                            aria-label={`${permission.label} — ${ROLE_LABELS[role]}`}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
              </Fragment>
            ))}
          </tbody>
          <tfoot className="border-t border-gray-200 bg-gray-50">
            <tr>
              <td className="px-4 py-3 text-sm text-gray-600">Enregistrer un rôle à la fois</td>
              {config.roles.map((role) => (
                <td key={role} className="px-3 py-3">
                  {role === 'super_admin' ? (
                    <span className="block text-center text-xs text-gray-500">Immuable</span>
                  ) : (
                    <div className="flex justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => void save(role)}
                        disabled={savingRole !== null}
                        className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                      >
                        <Save className="h-3.5 w-3.5" /> Enregistrer
                      </button>
                      <button
                        type="button"
                        onClick={() => void reset(role)}
                        disabled={savingRole !== null}
                        title="Réinitialiser les valeurs par défaut"
                        className="rounded-md border border-gray-300 p-1.5 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default RbacSettingsPage;
